import React, { useEffect, useState } from "react";
import moment from "moment";
import "moment/locale/nb";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "../../api/api";
import DashboardChart from "./DashboardChart";
import "./Dashboard.css";

const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentDate = moment().locale("En").format("MMM Do");

  const [chartData, setChartData] = useState([]);
  const [irrigNote, setIrrigNote] = useState({
    note: t("no_irrigation"),
    volume: 0,
  });
  const [activeSimulations, setActiveSimulations] = useState([]);
  const [allSimulations, setAllSimulations] = useState([]);
  const [simulationsLoading, setSimulationsLoading] = useState(true);

  useEffect(() => {
    const getActiveSimulations = async () => {
      try {
        const result = await api.get("/simulation/get-simulations");
        if (result.data.type === "success") {
          setActiveSimulations(result.data.simulations || []);
        }
      } catch (error) {
        console.error("Error loading active simulations:", error);
      }
    };

    const getAllSimulations = async () => {
      try {
        const result = await api.get("/simulation/get-all-simulations");
        if (result.data.type === "success") {
          setAllSimulations(result.data.simulations || []);
        }
      } catch (error) {
        console.error("Error loading simulations:", error);
      }
    };

    Promise.all([getActiveSimulations(), getAllSimulations()]).finally(() => {
      setSimulationsLoading(false);
    });
  }, []);

  useEffect(() => {
    const latestSimulation = activeSimulations[0];
    const latestResults = Array.isArray(latestSimulation?.result)
      ? latestSimulation.result
      : [];

    setChartData(
      latestResults.map((simulation) => ({
        bilan: simulation.bilan,
        dates: simulation.date,
      }))
    );

    const todayIrrigation = activeSimulations
      .flatMap((simulation) =>
        Array.isArray(simulation?.result) ? simulation.result : []
      )
      .find(
        (simulation) =>
          simulation.irrigationNbr === 1 &&
          moment(simulation.date).format("L") === moment().format("L")
      );

    setIrrigNote(
      todayIrrigation
        ? { note: t("irrigate"), volume: todayIrrigation.irrigation }
        : { note: t("no_irrigation"), volume: 0 }
    );
  }, [activeSimulations, t]);

  return (
    <main className="simulation-dashboard-page">
      <header className="simulation-dashboard-header">
        <div>
          <span className="simulation-dashboard-eyebrow">SMARTFARM</span>
          <h1>{t("Dashboard")}</h1>
          <p>
            {t("simulation_dashboard_subtitle", {
              defaultValue:
                "Follow your active simulations and today’s irrigation plan.",
            })}
          </p>
        </div>
        <span className="simulation-dashboard-date">
          <i className="material-icons" aria-hidden="true">&#xebcc;</i>
          {currentDate}
        </span>
      </header>

      <section className="simulation-dashboard-summary">
        <article className="simulation-dashboard-card simulation-dashboard-stat">
          <span className="simulation-dashboard-icon is-active" aria-hidden="true">
            <i className="material-icons">&#xe614;</i>
          </span>
          <div>
            <span className="simulation-dashboard-label">
              {t("active_simulations")}
            </span>
            {simulationsLoading ? (
              <span className="simulation-dashboard-skeleton" aria-hidden="true" />
            ) : (
              <strong>{activeSimulations.length}</strong>
            )}
          </div>
        </article>

        <article className="simulation-dashboard-card simulation-dashboard-stat">
          <span className="simulation-dashboard-icon is-total" aria-hidden="true">
            <i className="material-icons">&#xe0ee;</i>
          </span>
          <div>
            <span className="simulation-dashboard-label">
              {t("all_simulations")}
            </span>
            {simulationsLoading ? (
              <span className="simulation-dashboard-skeleton" aria-hidden="true" />
            ) : (
              <strong>{allSimulations.length}</strong>
            )}
          </div>
        </article>

        <article className="simulation-dashboard-card simulation-dashboard-today">
          <div className="simulation-dashboard-card-title">
            <div>
              <span className="simulation-dashboard-eyebrow">SMARTFARM</span>
              <h2>{t("today")}</h2>
            </div>
            <span>{currentDate}</span>
          </div>
          <div className="simulation-dashboard-irrigation">
            <span>{irrigNote.note}</span>
            <strong>
              {t("volume")} : {irrigNote.volume} m³
            </strong>
          </div>
          <button type="button" onClick={() => navigate("/Calendar")}>
            {t("see_all")}
          </button>
        </article>
      </section>

      <section className="simulation-dashboard-card simulation-dashboard-chart-card">
        <div className="simulation-dashboard-card-title">
          <div>
            <span className="simulation-dashboard-eyebrow">SMARTFARM</span>
            <h2>{t("latest_simulations")}</h2>
          </div>
        </div>
        <div className="simulation-dashboard-chart">
          <DashboardChart data={chartData} loading={simulationsLoading} />
        </div>
      </section>
    </main>
  );
};

export default Dashboard;
